// ============================================
// COMPONENT - CategoriaAlmacenSelector
// ============================================

import { useEffect, useRef, useState } from 'react';
import { FaCrown } from 'react-icons/fa6';
import { goToPremium } from '@/lib/premium';
import { MdAdd, MdCheck, MdClose, MdDeleteOutline, MdEditNote } from 'react-icons/md';
import { useCategoriasAlmacen, useCreateCategoriaAlmacen, useUpdateCategoriaAlmacen, useDeleteCategoriaAlmacen, useSession } from '@almacenes/core';

const NOMBRE_MIN = 2;
const NOMBRE_MAX = 50;

interface Props {
  /** ID de la categoría seleccionada, o null si ninguna */
  value: number | null;
  onChange: (id: number | null) => void;
  /** Deshabilitar interacciones (ej: mientras se guarda el formulario padre) */
  disabled?: boolean;
}

export const CategoriaAlmacenSelector = ({ value, onChange, disabled = false }: Props) => {
  const { data: session } = useSession();
  const hasPremiumAccess = session?.hasPremiumAccess ?? false;

  const { data: categorias = [], isLoading: cargandoCategorias } = useCategoriasAlmacen();
  const crearCategoria = useCreateCategoriaAlmacen();
  const actualizarCategoria = useUpdateCategoriaAlmacen();
  const eliminarCategoria = useDeleteCategoriaAlmacen();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [modoCrear, setModoCrear] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [errorNombre, setErrorNombre] = useState('');
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [nombreEditando, setNombreEditando] = useState('');
  const [errorEditar, setErrorEditar] = useState('');
  const [confirmarEliminarId, setConfirmarEliminarId] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const filtroInputRef = useRef<HTMLInputElement>(null);
  const nuevoNombreInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        cerrarDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus automático al abrir modo crear
  useEffect(() => {
    if (modoCrear) nuevoNombreInputRef.current?.focus();
  }, [modoCrear]);

  // Focus automático al entrar en modo editar
  useEffect(() => {
    if (editandoId !== null) editInputRef.current?.focus();
  }, [editandoId]);

  // Focus en filtro al abrir dropdown
  useEffect(() => {
    if (dropdownOpen && !modoCrear) {
      filtroInputRef.current?.focus();
    }
  }, [dropdownOpen, modoCrear]);

  const categoriaSeleccionada = categorias.find((c) => c.id === value) ?? null;

  const categoriasFiltradas = categorias.filter((c) =>
    c.nombre.toLowerCase().includes(filtro.toLowerCase().trim())
  );

  const cerrarDropdown = () => {
    setDropdownOpen(false);
    setFiltro('');
    setModoCrear(false);
    setNuevoNombre('');
    setErrorNombre('');
    setEditandoId(null);
    setNombreEditando('');
    setErrorEditar('');
    setConfirmarEliminarId(null);
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setNombreEditando('');
    setErrorEditar('');
  };

  const validarNombreEdicion = (nombre: string, idActual: number): string => {
    const trimmed = nombre.trim();
    if (!trimmed) return 'El nombre es obligatorio';
    if (trimmed.length < NOMBRE_MIN) return `Mínimo ${NOMBRE_MIN} caracteres`;
    if (trimmed.length > NOMBRE_MAX) return `Máximo ${NOMBRE_MAX} caracteres`;
    const yaExiste = categorias.some(
      (c) => c.id !== idActual && c.nombre.toLowerCase() === trimmed.toLowerCase()
    );
    if (yaExiste) return 'Ya existe una categoría con ese nombre';
    return '';
  };

  const handleGuardarEdicion = async (id: number) => {
    const error = validarNombreEdicion(nombreEditando, id);
    if (error) {
      setErrorEditar(error);
      editInputRef.current?.focus();
      return;
    }
    try {
      await actualizarCategoria.mutateAsync({ id, nombre: nombreEditando.trim() });
      cancelarEdicion();
    } catch {
      setErrorEditar('Error al actualizar. Intenta nuevamente.');
    }
  };

  const handleEliminar = async (id: number) => {
    try {
      await eliminarCategoria.mutateAsync(id);
      if (value === id) onChange(null);
      setConfirmarEliminarId(null);
    } catch {
      setConfirmarEliminarId(null);
    }
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    if (dropdownOpen) {
      cerrarDropdown();
    } else {
      setDropdownOpen(true);
    }
  };

  const handleSeleccionar = (id: number | null) => {
    onChange(id);
    cerrarDropdown();
  };

  // ── Validación del nombre de nueva categoría ──────────────────────────────

  const validarNombre = (nombre: string): string => {
    const trimmed = nombre.trim();
    if (!trimmed) return 'El nombre es obligatorio';
    if (trimmed.length < NOMBRE_MIN) return `Mínimo ${NOMBRE_MIN} caracteres`;
    if (trimmed.length > NOMBRE_MAX) return `Máximo ${NOMBRE_MAX} caracteres`;
    const yaExiste = categorias.some(
      (c) => c.nombre.toLowerCase() === trimmed.toLowerCase()
    );
    if (yaExiste) return 'Ya existe una categoría con ese nombre';
    return '';
  };

  const handleNuevoNombreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // No permitir más de NOMBRE_MAX caracteres
    if (val.length > NOMBRE_MAX) return;
    setNuevoNombre(val);
    // Limpiar error mientras escribe
    if (errorNombre) setErrorNombre('');
  };

  const handleCrear = async () => {
    const error = validarNombre(nuevoNombre);
    if (error) {
      setErrorNombre(error);
      nuevoNombreInputRef.current?.focus();
      return;
    }

    try {
      const nueva = await crearCategoria.mutateAsync(nuevoNombre.trim());
      onChange(nueva.id);
      cerrarDropdown();
    } catch {
      setErrorNombre('Error al crear la categoría. Intenta nuevamente.');
    }
  };

  const handleNuevoNombreKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCrear();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setModoCrear(false);
      setNuevoNombre('');
      setErrorNombre('');
    }
  };

  // ── Render: usuario sin premium ───────────────────────────────────────────

  if (!hasPremiumAccess) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
        <FaCrown className="text-purple-500 flex-shrink-0" />
        <span className="text-sm text-gray-500">Disponible con suscripción Premium</span>
        <button
          type="button"
          onClick={() => goToPremium()}
          className="ml-auto text-xs font-semibold text-purple-600 hover:underline flex-shrink-0"
        >
          Ver planes
        </button>
      </div>
    );
  }

  // ── Render: premium ───────────────────────────────────────────────────────

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        disabled={disabled || cargandoCategorias}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-left flex items-center justify-between transition-colors hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
        style={{ borderColor: dropdownOpen ? '#43BFFF' : '#E5E7EB' }}
      >
        <span className={categoriaSeleccionada ? 'text-gray-800' : 'text-gray-400'}>
          {cargandoCategorias
            ? 'Cargando categorías...'
            : (categoriaSeleccionada?.nombre ?? 'Sin categoría')}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${dropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">

          {/* ── Modo crear nueva categoría ── */}
          {modoCrear ? (
            <div className="p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Nueva categoría
              </p>
              <input
                ref={nuevoNombreInputRef}
                type="text"
                value={nuevoNombre}
                onChange={handleNuevoNombreChange}
                onKeyDown={handleNuevoNombreKeyDown}
                placeholder="Nombre de la categoría"
                maxLength={NOMBRE_MAX}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${
                  errorNombre ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-blue-300'
                }`}
              />
              {/* Contador y error */}
              <div className="flex items-center justify-between">
                <span className={`text-xs ${errorNombre ? 'text-red-500' : 'text-gray-400'}`}>
                  {errorNombre || `${nuevoNombre.length}/${NOMBRE_MAX}`}
                </span>
              </div>
              {/* Botones de acción */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCrear}
                  disabled={crearCategoria.isPending || !nuevoNombre.trim()}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#43BFFF' }}
                >
                  {crearCategoria.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <MdCheck className="text-base" />
                  )}
                  {crearCategoria.isPending ? 'Creando...' : 'Crear'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModoCrear(false);
                    setNuevoNombre('');
                    setErrorNombre('');
                  }}
                  disabled={crearCategoria.isPending}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <MdClose className="text-base" />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Buscador ── */}
              <div className="p-2 border-b border-gray-100">
                <input
                  ref={filtroInputRef}
                  type="text"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Buscar categoría..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-300"
                />
              </div>

              {/* ── Lista ── */}
              <ul className="max-h-48 overflow-y-auto">
                {/* Opción "Sin categoría" */}
                <li>
                  <button
                    type="button"
                    onClick={() => handleSeleccionar(null)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      value === null ? 'bg-blue-50 font-semibold' : 'text-gray-400'
                    }`}
                  >
                    Sin categoría
                  </button>
                </li>

                {/* Categorías filtradas */}
                {categoriasFiltradas.map((cat) => (
                  <li key={cat.id}>
                    {/* Modo edición inline */}
                    {editandoId === cat.id ? (
                      <div className="px-3 py-2 space-y-1">
                        <input
                          ref={editInputRef}
                          type="text"
                          value={nombreEditando}
                          maxLength={NOMBRE_MAX}
                          onChange={(e) => {
                            if (e.target.value.length <= NOMBRE_MAX) setNombreEditando(e.target.value);
                            if (errorEditar) setErrorEditar('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleGuardarEdicion(cat.id); }
                            if (e.key === 'Escape') { e.preventDefault(); cancelarEdicion(); }
                          }}
                          className={`w-full px-2 py-1 text-sm border rounded focus:outline-none ${
                            errorEditar ? 'border-red-400' : 'border-gray-300 focus:border-blue-300'
                          }`}
                        />
                        {errorEditar && <p className="text-xs text-red-500">{errorEditar}</p>}
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleGuardarEdicion(cat.id)}
                            disabled={actualizarCategoria.isPending}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white rounded disabled:opacity-50"
                            style={{ backgroundColor: '#43BFFF' }}
                          >
                            <MdCheck /> Guardar
                          </button>
                          <button
                            type="button"
                            onClick={cancelarEdicion}
                            disabled={actualizarCategoria.isPending}
                            className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            <MdClose />
                          </button>
                        </div>
                      </div>
                    ) : confirmarEliminarId === cat.id ? (
                      /* Confirmación de eliminación inline */
                      <div className="px-3 py-2 flex items-center justify-between gap-2 bg-red-50">
                        <span className="text-xs text-red-700 flex-1">¿Eliminar "{cat.nombre}"?</span>
                        <button
                          type="button"
                          onClick={() => handleEliminar(cat.id)}
                          disabled={eliminarCategoria.isPending}
                          className="px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50"
                        >
                          {eliminarCategoria.isPending ? '...' : 'Sí'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmarEliminarId(null)}
                          disabled={eliminarCategoria.isPending}
                          className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      /* Fila normal con acciones al hover */
                      <div className="group flex items-center gap-1 px-2 hover:bg-gray-50 transition-colors">
                        <button
                          type="button"
                          onClick={() => handleSeleccionar(cat.id)}
                          className={`flex-1 text-left py-2 text-sm ${
                            value === cat.id ? 'font-semibold' : ''
                          }`}
                          style={value === cat.id ? { color: '#43BFFF' } : {}}
                        >
                          {cat.nombre}
                        </button>
                        <button
                          type="button"
                          title="Editar"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditandoId(cat.id);
                            setNombreEditando(cat.nombre);
                            setErrorEditar('');
                            setConfirmarEliminarId(null);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-500 transition-opacity"
                        >
                          <MdEditNote className="text-base" />
                        </button>
                        <button
                          type="button"
                          title="Eliminar"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmarEliminarId(cat.id);
                            setEditandoId(null);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                        >
                          <MdDeleteOutline className="text-base" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}

                {/* Sin resultados en la búsqueda */}
                {filtro && categoriasFiltradas.length === 0 && (
                  <li className="px-4 py-3 text-sm text-gray-400 text-center">
                    Sin resultados para "{filtro}"
                  </li>
                )}

                {/* Sin categorías creadas */}
                {!filtro && categorias.length === 0 && (
                  <li className="px-4 py-4 text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      Aún no tienes categorías creadas
                    </p>
                  </li>
                )}
              </ul>

              {/* ── Botón crear nueva ── */}
              <div className="border-t border-gray-100 p-2">
                <button
                  type="button"
                  onClick={() => {
                    setModoCrear(true);
                    setFiltro('');
                    // Pre-llenar con el texto del filtro si el usuario escribió algo
                    if (filtro.trim()) setNuevoNombre(filtro.trim());
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md transition-colors hover:bg-gray-50"
                  style={{ color: '#43BFFF' }}
                >
                  <MdAdd className="text-base flex-shrink-0" />
                  {filtro.trim()
                    ? `Crear "${filtro.trim()}"`
                    : 'Crear nueva categoría'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
